import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { CHART_INK, SERIES_COLORS } from "../../../../../utils/chartPalette";
import { useChartExport } from "../useChartExport";
import { cellValue } from "../PivotTable/pivotModel";
import { niceTicks } from "./SeriesChart";

// Year over year: one line per year and species across January to December,
// a point per week. Weeks are fixed blocks of the calendar - week 1 is
// January 1 to 7 in every year - so a week means the same dates each year and
// the lines line up season for season.
//
// The usual question is one or two species across the years, so the chart
// opens on the report's most-caught species, years are told apart by color and
// species by line style. A year keeps its color whatever the report covers:
// this year is the palette's first color, last year its second, and so on.

const NUMBER = new Intl.NumberFormat();
const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
export const DEFAULT_SPECIES = 1;

// a common year's dates for each block, for the axis
const blockStart = (n) => new Date(Date.UTC(2025, 0, 1 + (n - 1) * 7));
export const weekLabel = (n) => DAY.format(blockStart(n));
export const weekSpanLabel = (n) => {
  const end = n === 53 ? new Date(Date.UTC(2025, 11, 31)) : new Date(blockStart(n).getTime() + 6 * 86400000);
  return `${DAY.format(blockStart(n))} – ${DAY.format(end)}`;
};

// the picked species, in the order picked: solid, dashed, dotted, dash-dot
export const SPECIES_STYLES = [
  { dash: undefined, width: 2.5 },
  { dash: "7 4", width: 2 },
  { dash: "2 3", width: 2 },
  { dash: "10 3 2 3", width: 2 },
];
export const MAX_SPECIES = SPECIES_STYLES.length;
const STYLE_NAMES = ["solid", "dashed", "dotted", "dash-dot"];

/** A year's color: this year the palette's first, last year its second, ... */
export const yearColor = (year, today = new Date()) => {
  const age = Math.max(0, today.getFullYear() - Number(year));
  return SERIES_COLORS[age % SERIES_COLORS.length];
};

/**
 * The weeks of each year the data can speak for: the current year stops at
 * this week, and the first year starts at its first week with any specimens,
 * so weeks not yet lived or before records began are gaps, not zeros.
 * Returns {year: [first, last]}.
 */
export const coveredWeeks = (model, today = new Date()) => {
  const years = [...new Set(model.columns.map((c) => c.label[0]))].sort();
  const thisYear = String(today.getUTCFullYear());
  const thisWeek = Math.floor((today - Date.UTC(today.getUTCFullYear(), 0, 1)) / (7 * 86400000)) + 1;
  return Object.fromEntries(
    years.map((year, i) => {
      let first = 1;
      if (i === 0) {
        const cols = model.columns.map((c, ci) => (c.label[0] === year ? ci : -1)).filter((ci) => ci >= 0);
        const firstRow = model.rows.findIndex((_, r) => cols.some((c) => cellValue(model, r, c) > 0));
        if (firstRow >= 0) first = Number(model.rows[firstRow].key[0]);
      }
      return [year, [first, year === thisYear ? thisWeek : 53]];
    })
  );
};

const LineSample = ({ color, style }) => (
  <svg width="28" height="10" aria-hidden="true">
    <line x1="1" y1="5" x2="27" y2="5" stroke={color} strokeWidth={style.width} strokeDasharray={style.dash} />
  </svg>
);

/**
 * Lines from a pivot of weekOfYear rows by [year, species] columns.
 */
export const YearlyTrendsChart = ({ model, title, registerChartExport }) => {
  // the species and years in the data, species by total, years newest first
  const { species, years } = useMemo(() => {
    const totals = new Map();
    const yearSet = new Set();
    model.columns.forEach((col, c) => {
      const [year, name] = col.label;
      yearSet.add(year);
      if (col.category[1] !== "species") return;
      totals.set(name, (totals.get(name) ?? 0) + (model.columnTotals[c] ?? 0));
    });
    return {
      species: [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name),
      years: [...yearSet].sort().reverse(),
    };
  }, [model]);

  const [picked, setPicked] = useState(null);
  // a new report starts again from its most-caught species
  useEffect(() => setPicked(null), [model]);
  const selected = picked ?? species.slice(0, DEFAULT_SPECIES);
  const styleOf = (name) => SPECIES_STYLES[selected.indexOf(name)] ?? SPECIES_STYLES[0];

  const series = useMemo(
    () =>
      selected.flatMap((name, i) =>
        years.map((year) => ({
          key: `${year} ${name}`,
          species: name,
          year,
          color: yearColor(year),
          style: SPECIES_STYLES[i],
          column: model.columns.findIndex((col) => col.label[0] === year && col.label[1] === name),
        }))
      ).filter((s) => s.column >= 0),
    [selected, years, model]
  );

  const covered = useMemo(() => coveredWeeks(model), [model]);
  const data = useMemo(
    () =>
      model.rows.map((row, r) => {
        const week = Number(row.key[0]);
        const point = { week, name: weekLabel(week) };
        series.forEach((s) => {
          const [first, last] = covered[s.year];
          point[s.key] = week < first || week > last ? null : cellValue(model, r, s.column);
        });
        return point;
      }),
    [model, series, covered]
  );

  const peak = Math.max(0, ...data.flatMap((d) => series.map((s) => d[s.key] ?? 0)));
  const yTicks = niceTicks(peak);

  const getExportSpec = useCallback(
    () => ({
      title,
      // swatches carry color only: the years, then each species' line style in words
      legend: [
        ...years.map((year) => ({ label: year, color: yearColor(year) })),
        ...selected.map((name, i) => ({ label: `${name} (${STYLE_NAMES[i]} line)`, color: CHART_INK.primary })),
      ],
      dataRows: [
        ["Week", ...model.columns.map((c) => `${c.label[1]} ${c.label[0]}`)],
        ...model.rows.map((row, r) => [
          weekSpanLabel(Number(row.key[0])),
          ...model.columns.map((_, c) => cellValue(model, r, c)),
        ]),
      ],
    }),
    [title, years, selected, model]
  );
  useChartExport(registerChartExport, getExportSpec);

  if (!species.length) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography color="text.secondary">No specimens match these filters.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, px: { xs: 2, md: 3 }, py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, mb: 1, flexWrap: "wrap" }}>
        <Autocomplete
          multiple
          size="small"
          options={species}
          value={selected}
          onChange={(_, value) => setPicked(value.slice(0, MAX_SPECIES))}
          // each species needs a line style of its own
          getOptionDisabled={(name) => selected.length >= MAX_SPECIES && !selected.includes(name)}
          disableCloseOnSelect
          renderTags={(value, getTagProps) =>
            value.map((name, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  size="small"
                  label={name}
                  icon={
                    <Box component="span" sx={{ display: "flex", ml: "6px !important" }}>
                      <LineSample color={CHART_INK.primary} style={styleOf(name)} />
                    </Box>
                  }
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Species"
              placeholder={selected.length ? "" : "Pick species"}
              helperText={selected.length >= MAX_SPECIES ? `Up to ${MAX_SPECIES} species, one line style each` : " "}
            />
          )}
          sx={{ minWidth: 320, flex: "1 1 320px", maxWidth: 640 }}
        />
        <Box component="ul" aria-label="Years" sx={{ display: "flex", gap: 2, m: 0, p: 0, listStyle: "none", alignItems: "center", minHeight: 40 }}>
          {years.map((year) => (
            <Box component="li" key={year} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12 }}>
              <LineSample color={yearColor(year)} style={SPECIES_STYLES[0]} />
              {year}
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis
              dataKey="name"
              interval="preserveStartEnd"
              minTickGap={28}
              height={32}
              tickLine={{ stroke: CHART_INK.axis }}
              axisLine={{ stroke: CHART_INK.axis }}
              tick={{ fill: CHART_INK.secondary, fontSize: 12 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: CHART_INK.secondary, fontSize: 12 }}
              tickFormatter={(v) => NUMBER.format(v)}
              width={56}
              ticks={yTicks}
              domain={[0, yTicks[yTicks.length - 1]]}
              allowDecimals={false}
              label={{ value: "Total count", angle: -90, position: "insideLeft", fill: CHART_INK.secondary, fontSize: 12, dy: 40 }}
            />
            <Tooltip
              cursor={{ stroke: CHART_INK.axis }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const point = payload[0].payload;
                return (
                  <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, boxShadow: 2, fontSize: 13 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: 13, mb: 0.5 }}>Week of {weekSpanLabel(point.week)}</Typography>
                    {series.filter((s) => point[s.key] != null).map((s) => (
                      <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LineSample color={s.color} style={s.style} />
                        <span>
                          {s.species}, {s.year}
                        </span>
                        <Box component="span" sx={{ ml: "auto", pl: 2, fontVariantNumeric: "tabular-nums" }}>
                          {NUMBER.format(point[s.key] ?? 0)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              }}
            />
            {series.map((s) => (
              <Line
                key={s.key}
                dataKey={s.key}
                name={`${s.species}, ${s.year}`}
                type="linear"
                stroke={s.color}
                strokeWidth={s.style.width}
                strokeDasharray={s.style.dash}
                dot={false}
                activeDot={{ r: 4, stroke: "#ffffff", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 1 }}>
        Week of the year (January 1 to 7 is week 1 every year)
      </Typography>
    </Box>
  );
};
